import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Check } from "lucide-react";
import useEscape from "../lib/useEscape";
import api, { apiErrorMessage } from "../lib/api";
import { toast } from "sonner";

/**
 * Intended Purchase MONTH picker (used both for joining a room and for
 * filtering the chat by month).
 *
 * Behaviour:
 * - Shows a 12-month grid per year, navigable between current month and
 *   12 months ahead.
 * - Each cell shows the month label + ✊ buyer-count for that month in
 *   the requester's scope.
 * - Picker stores the chosen value as a canonical 'YYYY-MM' string. Both
 *   `onSaved(month)` (default save flow) and `onPick(month | 'all')`
 *   (pickerMode filter flow) emit the same canonical format.
 */
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function toMonthKey(d) {
  if (!d) return "";
  // Hardening: bail out early on sentinel / invalid inputs like "pending".
  // Date.parse('pending-01') resolves to Jan 1 2001 in V8, which previously
  // caused the IntentDateModal to open at year 2001. We accept only canonical
  // forms here.
  if (typeof d === "string") {
    const s = d.trim();
    if (/^\d{4}-\d{2}$/.test(s)) return s;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 7);
    return "";
  }
  if (!d || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function monthLabel(month) {
  if (!month) return "";
  const [y, m] = month.split("-");
  if (!y || !m) return month;
  const idx = parseInt(m, 10) - 1;
  if (isNaN(idx) || idx < 0 || idx > 11) return month;
  return `${MONTHS_SHORT[idx]} ${y}`;
}

export default function IntentDateModal({
  productId,
  city,
  onClose,
  onSaved,
  onPick,
  onDecideLater,
  pickerMode = false,
  initialMyDate = null,
  productName = "this product",
  title,
  submitLabel,
}) {
  const today = useMemo(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; }, []);
  const todayKey = useMemo(() => toMonthKey(today), [today]);
  useEscape(!!onClose, onClose);

  // Dialog behaviour: focus on open, Tab trap, body scroll lock, focus restore on close.
  const dialogRef = useRef(null);
  useEffect(() => {
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key !== "Tab" || !dialogRef.current) return;
      const items = [...dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.offsetParent !== null);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0]; const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialogRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
      else if (!dialogRef.current.contains(active)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (prevFocus && typeof prevFocus.focus === "function") prevFocus.focus({ preventScroll: true });
    };
  }, []);
  const maxMonth = useMemo(() => {
    const d = new Date(today); d.setMonth(d.getMonth() + 12); return d;
  }, [today]);
  const maxKey = useMemo(() => toMonthKey(maxMonth), [maxMonth]);

  const initialMonth = initialMyDate === "ASAP" ? "ASAP" : (initialMyDate ? toMonthKey(initialMyDate) : "");
  const [viewYear, setViewYear] = useState(() => {
    if (initialMonth && initialMonth !== "ASAP") return parseInt(initialMonth.split("-")[0], 10);
    return today.getFullYear();
  });
  const [counts, setCounts] = useState({}); // { 'YYYY-MM': count }
  const [asapCount, setAsapCount] = useState(0);
  const [picked, setPicked] = useState(initialMonth);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [asapSaving, setAsapSaving] = useState(false);

  const decideLater = async () => {
    if (pickerMode) { onPick?.("all"); return; }
    setDeciding(true);
    try {
      await api.post(`/products/${productId}/join-pending`, { city: city || "" });
      toast.success("Joined the room — you can browse chats and pick your month anytime.");
      try { window.dispatchEvent(new CustomEvent("bhega:changed", { detail: { productId } })); } catch (_) { /* noop */ }
      onDecideLater?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not join the room"));
    } finally {
      setDeciding(false);
    }
  };

  const joinAsap = async () => {
    if (pickerMode) { onPick?.("ASAP"); return; }
    setAsapSaving(true);
    try {
      await api.post(`/products/${productId}/intent-date`, { date: "ASAP", city: city || "" });
      toast.success("You joined the As-soon-as-possible room.");
      try { window.dispatchEvent(new CustomEvent("bhega:changed", { detail: { productId } })); } catch (_) { /* noop */ }
      onSaved?.("ASAP");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not join the ASAP room"));
    } finally {
      setAsapSaving(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/products/${productId}/intent-calendar`, { params: city ? { city } : {} })
      .then((r) => {
        if (!active) return;
        const map = {};
        (r.data.dates || []).forEach((d) => {
          const key = (d.month || d.date || "").slice(0, 7);
          if (key) map[key] = (map[key] || 0) + (d.count || 0);
        });
        setCounts(map);
        setAsapCount(r.data.asap_count || 0);
        if (!initialMonth && r.data.my_date) {
          if (r.data.my_date === "ASAP") {
            setPicked("ASAP");
          } else {
            const k = toMonthKey(r.data.my_date);
            if (k) {
              setPicked(k);
              setViewYear(parseInt(k.split("-")[0], 10));
            }
          }
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [productId, city, initialMonth]);

  const canPrev = viewYear > today.getFullYear();
  const canNext = viewYear < maxMonth.getFullYear();
  const prev = () => { if (canPrev) setViewYear((y) => y - 1); };
  const next = () => { if (canNext) setViewYear((y) => y + 1); };

  const submit = async () => {
    if (!picked) { toast.error("Pick a month to continue"); return; }
    if (picked === "ASAP") { return joinAsap(); }
    if (pickerMode) { onPick?.(picked); return; }
    setSaving(true);
    try {
      await api.post(`/products/${productId}/intent-date`, { date: picked, city: city || "" });
      toast.success(`You're in ✊ — grouped with buyers purchasing in ${monthLabel(picked)}`);
      try { window.dispatchEvent(new CustomEvent("bhega:changed", { detail: { productId } })); } catch (_) { /* noop */ }
      onSaved?.(picked);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save month"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[100] bg-background/60 flex justify-center p-4 ${onClose ? "items-end sm:items-center" : "items-center"}`}
      onClick={onClose || undefined}
      data-testid="intent-modal-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="intent-modal-title"
        tabIndex={-1}
        className="bg-card w-full max-w-xl border border-border shadow-[var(--bhega-elevation)] max-h-[85dvh] overflow-y-auto slide-up focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        data-testid="intent-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="eyebrow text-xs text-brand mb-1">{pickerMode ? "Filter" : "Choose month"}</div>
            <h3 id="intent-modal-title" className="font-heading font-extrabold text-xl text-foreground leading-tight">{title || (pickerMode ? "Filter chat by month" : "Intended month of purchase")}</h3>
            <p className="text-xs text-muted-foreground mt-1.5">
              {pickerMode ? (
                <>Pick any month to view only chats from buyers planning to purchase in that month.</>
              ) : (
                <>Pick the month you plan to buy <strong className="text-foreground">{productName}</strong>. We unite buyers who picked the same month.</>
              )}
            </p>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-1.5 hover:bg-foreground/10 rounded-full text-muted-foreground hover:text-foreground transition-all" data-testid="intent-modal-close" aria-label="Close">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prev} disabled={!canPrev} className="p-2 rounded-full border border-border hover:border-[#FFBE06] disabled:opacity-30 text-foreground transition-all" data-testid="intent-prev-year" aria-label="Previous year">
              <ChevronLeft size={16} />
            </button>
            <div className="font-heading font-extrabold text-3xl text-foreground tracking-tight" data-testid="intent-year-label">{viewYear}</div>
            <button onClick={next} disabled={!canNext} className="p-2 rounded-full border border-border hover:border-[#FFBE06] disabled:opacity-30 text-foreground transition-all" data-testid="intent-next-year" aria-label="Next year">
              <ChevronRight size={16} />
            </button>
          </div>

          {loading ? (
            <p className="text-center text-sm text-muted-foreground py-10">Loading months…</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {MONTHS_SHORT.map((label, idx) => {
                const key = `${viewYear}-${String(idx + 1).padStart(2, "0")}`;
                const past = key < todayKey;
                const beyond = key > maxKey;
                const disabled = past || beyond;
                const c = counts[key] || 0;
                const isPicked = picked === key;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={disabled}
                    onClick={() => setPicked(key)}
                    data-testid={`intent-month-${key}`}
                    className={`relative border rounded-none text-sm py-4 px-2 flex flex-col items-center gap-1 transition-all
                      ${disabled ? "border-border text-muted-foreground/50 cursor-not-allowed bg-background/30" :
                        isPicked ? "border-[#FFBE06] bg-[#FFBE06] text-black font-extrabold glow-yellow" :
                        "border-border hover:border-[#FFBE06]/50 text-foreground hover:bg-[#FFBE06]/10"}`}
                  >
                    <span className="font-semibold">{label}</span>
                    {!disabled && c > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-xs font-bold">
                        <span aria-hidden>✊</span>{c}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-4 text-xs text-muted-foreground flex items-center gap-2">
            <span className="inline-block w-3 h-3 bg-[#FFBE06] border border-border" /> Your pick
            <span className="ml-3 inline-flex items-center gap-1"><span aria-hidden>✊</span> Buyers in that month</span>
          </div>

          {/* Equal-width action buttons below the month grid — 'I will decide later' + 'As soon as possible' */}
          <div className="mt-5 grid grid-cols-2 gap-2" data-testid="intent-alt-actions">
            <button
              type="button"
              onClick={decideLater}
              disabled={deciding || saving || asapSaving}
              className={`py-3 px-3 rounded-none border text-sm font-bold transition-all
                ${pickerMode
                  ? "border-border hover:border-[#FFBE06]/60 text-foreground hover:bg-[#FFBE06]/10"
                  : "border-border hover:border-[#FFBE06]/60 text-foreground hover:bg-[#FFBE06]/10"}`}
              data-testid="intent-decide-later-alt"
            >
              {deciding ? "Joining…" : (pickerMode ? "Show all months" : "I will decide later")}
            </button>
            <button
              type="button"
              onClick={() => { if (pickerMode) onPick?.("ASAP"); else setPicked("ASAP"); }}
              disabled={deciding || saving || asapSaving}
              className={`py-3 px-3 rounded-none border text-sm font-bold transition-all flex flex-col items-center justify-center gap-0.5
                ${picked === "ASAP"
                  ? "border-[#FFBE06] bg-[#FFBE06] text-black glow-yellow"
                  : "border-[#FFBE06] text-brand hover:bg-[#FFBE06]/10"}`}
              data-testid="intent-asap"
            >
              <span>As soon as possible</span>
              {asapCount > 0 && (
                <span className={`text-xs font-bold ${picked === "ASAP" ? "text-black/70" : "text-brand"}`}>
                  ✊ {asapCount} buyer{asapCount === 1 ? "" : "s"}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border flex items-center justify-between gap-2 sticky bottom-0 bg-card">
          <div className="text-xs text-muted-foreground" data-testid="intent-picked-label">
            {picked === "ASAP"
              ? <>Selected: <strong>As soon as possible</strong></>
              : picked
                ? <>Selected: <strong>{monthLabel(picked)}</strong></>
                : (pickerMode ? "Pick a month, ASAP, or show all" : "Pick a month or ASAP to continue")}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={submit}
              disabled={!picked || saving || asapSaving}
              className="btn-primary text-sm disabled:opacity-50"
              data-testid="intent-submit"
            >
              <Check size={14} /> {submitLabel || (
                picked === "ASAP"
                  ? (asapSaving ? "Joining…" : (pickerMode ? "Filter ASAP" : "Join ASAP room"))
                  : (saving ? "Saving…" : (pickerMode ? "Apply month filter" : (initialMyDate ? "Update month" : "Continue to room")))
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { monthLabel as formatMonthLabel, toMonthKey };
